import React from "react";
import DateRangePicker, { DateRange } from "../common/DateRangePicker";
import { ROAMING_INTERVALS, RoamingInterval } from "../../hooks/realtime/useRoamingPulse";

export type RealtimeView = "timeline" | "country" | "roaming" | "baseline" | "report";

const LABELS: Record<RealtimeView, string> = {
  timeline: "Timeline",
  country: "By country",
  roaming: "Roaming pulse",
  baseline: "Population baseline",
  report: "Day report",
};

// Phone labels: five full labels don't fit across a phone, and a strip that
// scrolls sideways hides the last tabs, so every tab gets a one-word name.
const SHORT: Record<RealtimeView, string> = {
  timeline: "Timeline",
  country: "Countries",
  roaming: "Roaming",
  baseline: "Baseline",
  report: "Report",
};

export const isRealtimeView = (v: string | null): v is RealtimeView => !!v && v in LABELS;

/**
 * The view tabs of a realtime dashboard. On a phone they span the full width
 * with short labels, so every tab is visible without scrolling.
 */
export const RealtimeTabs: React.FC<{
  view: RealtimeView;
  onView: (v: RealtimeView) => void;
  /** Emergency only: adds "Population baseline". */
  showBaseline?: boolean;
  /** Project dashboards: adds the last tab, "Day report". */
  showReport?: boolean;
  className?: string;
}> = ({ view, onView, showBaseline = false, showReport = false, className = "" }) => (
  <div className={className}>
    <div role="tablist" className="flex w-full rounded-lg border border-paper-300 bg-surface p-0.5 md:inline-flex md:w-auto">
      {(Object.keys(LABELS) as RealtimeView[])
        .filter((v) => (v !== "baseline" || showBaseline) && (v !== "report" || showReport))
        .map((v) => (
          <button
            key={v}
            role="tab"
            aria-selected={view === v}
            onClick={() => onView(v)}
            aria-label={LABELS[v]}
            className={`min-w-0 flex-1 whitespace-nowrap rounded-md px-1 py-1.5 text-xs font-medium transition-colors md:flex-none md:px-4 md:text-sm ${
              view === v ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
            }`}
          >
            <span className="md:hidden">{SHORT[v]}</span>
            <span className="hidden md:inline">{LABELS[v]}</span>
          </button>
        ))}
    </div>
  </div>
);

/**
 * The control that scopes the active view — a date range for the historical
 * tabs, or the comparison interval and a Live badge for the always-now roaming
 * board. Sits right above the view's cards, so it is unambiguous which views
 * the range applies to. Nothing for views that bring their own controls.
 */
export const RealtimeViewControls: React.FC<{
  view: RealtimeView;
  onDateRange: (r: DateRange) => void;
  rangeLabel: string;
  roamingInterval: RoamingInterval;
  onRoamingInterval: (m: RoamingInterval) => void;
}> = ({ view, onDateRange, rangeLabel, roamingInterval, onRoamingInterval }) =>
  view === "baseline" || view === "report" ? null : view === "roaming" ? (
    <div className="flex flex-col items-end gap-1">
      <div className="inline-flex items-center gap-2">
        <span className="text-xs text-ink-muted">change over</span>
        <div className="inline-flex rounded-lg border border-paper-300 p-0.5">
          {ROAMING_INTERVALS.map((m) => (
            <button
              key={m}
              onClick={() => onRoamingInterval(m)}
              className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                roamingInterval === m ? "bg-brand-500 text-on-brand" : "text-ink-muted hover:bg-paper-100"
              }`}
            >
              {m} min
            </button>
          ))}
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-ok-ink">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-ok" />
        Live · refreshes every minute
      </span>
    </div>
  ) : (
    <div className="flex flex-col items-end gap-1">
      <DateRangePicker defaultPreset="24h" onChange={onDateRange} />
      <span className="text-xs text-ink-muted">
        Showing <span className="font-medium text-ink">{rangeLabel}</span>
      </span>
    </div>
  );

/**
 * The one filter row above a single-area realtime dashboard: view tabs on the
 * left and the active view's control on the right.
 */
const RealtimeViewSwitch: React.FC<{
  view: RealtimeView;
  onView: (v: RealtimeView) => void;
  dateRange: DateRange;
  onDateRange: (r: DateRange) => void;
  rangeLabel: string;
  roamingInterval: RoamingInterval;
  onRoamingInterval: (m: RoamingInterval) => void;
  /** Emergency only: adds the last tab, "Population baseline". */
  showBaseline?: boolean;
}> = ({ view, onView, onDateRange, rangeLabel, roamingInterval, onRoamingInterval, showBaseline = false }) => (
  <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
    <RealtimeTabs view={view} onView={onView} showBaseline={showBaseline} />
    <RealtimeViewControls
      view={view}
      onDateRange={onDateRange}
      rangeLabel={rangeLabel}
      roamingInterval={roamingInterval}
      onRoamingInterval={onRoamingInterval}
    />
  </div>
);

export default RealtimeViewSwitch;
